import {
  GET_RESOURCE_TASKS_REQUEST,
  GET_RESOURCE_TASKS_SUCCESS,
  GET_RESOURCE_TASKS_ERROR,
  GET_RESOURCE_TASKS_CHUNK,
  SET_RESOURCE_TASKS_SCOPE,
  GET_RESOURCE_TASKS_FROM_CACHE,
  CREATE_RESOURCE_TASK_REQUEST,
  CREATE_RESOURCE_TASK_SUCCESS,
  CREATE_RESOURCE_TASK_ERROR,
  UPDATE_RESOURCE_TASK_REQUEST,
  UPDATE_RESOURCE_TASK_SUCCESS,
  UPDATE_RESOURCE_TASK_ERROR,
  DELETE_RESOURCE_TASK_REQUEST,
  DELETE_RESOURCE_TASK_SUCCESS,
  DELETE_RESOURCE_TASK_ERROR,
  ASSIGN_RESOURCE_TASK_SUCCESS,
  ASSIGN_RESOURCE_TASK_ERROR,
  ASSIGN_RESOURCE_TASK_REQUEST,
  CHECK_IS_MANAGER_SUCCESS,
  CHECK_IS_MANAGER_ERROR,
  UPDATE_ACTIVE,
  GET_RESOURCE_TASKS_BY_SCENARIO_ID_REQUEST,
  GET_RESOURCE_TASKS_BY_SCENARIO_ID_SUCCESS,
  GET_RESOURCE_TASKS_BY_SCENARIO_ID_ERROR,
  TOP_DOWN_RESOURCE_TASK_UPDATE,
  GET_RESOURCE_TASKS_DASHBOARD_REQUEST,
  GET_RESOURCE_TASKS_DASHBOARD_SUCCESS,
  GET_RESOURCE_TASKS_DASHBOARD_ERROR,
  GET_RESOURCE_TASKS_CUSTOM_COLUMNS_REQUEST,
  GET_RESOURCE_TASKS_CUSTOM_COLUMNS_SUCCESS,
  GET_RESOURCE_TASKS_CUSTOM_COLUMNS_ERROR,
  GET_RESOURCE_TASKS_CUSTOM_COLUMN_NAMES_REQUEST,
  GET_RESOURCE_TASKS_CUSTOM_COLUMN_NAMES_SUCCESS,
  GET_RESOURCE_TASKS_CUSTOM_COLUMN_NAMES_ERROR,
  ADD_RT_CUSTOM_COLUMNS,
  UPDATE_RT_CUSTOM_COLUMN_VALUE,
  UPDATE_USER_TASK,
  UPDATE_USER_TASK_STATUS_AND_PROGRESS,
  SET_ALL_AS_ACTIVE,
  SET_ALL_AS_INACTIVE,
  DELETE_BATCH_OF_RESOURCE_TASKS,
} from "../types";

import produce from "immer";
import { rtRowInWindow } from "../../utils/periodWindow/periodWindow";

const initialState = {
  tasks: [],
  // Per-session cache of look-ahead Gantt resource tasks keyed by period, so switching back to
  // an already-loaded period serves from memory instead of refetching — keeps the Gantt (bottom)
  // in sync with the WorkPackage grid (top), which already caches.
  tasksPool: [],
  tasksCoverage: null,
  tasksWindow: null,
  tasksCache: {},
  tasksCacheKey: null,
  dashboard: [],
  isFetching: false,
  isDashboardFetching: false,
  isFetchingColumns: false,
  isFetchingColumnNames: false,
  isUpdating: false,
  isManager: false,
  customColumns: [],
  customColumnNames: [],
  error: "",
};
// Any mutation of resource tasks must invalidate the per-session period cache, otherwise
// switching away and back would serve a stale snapshot (missing the edit/add/delete).
const TASKS_CACHE_INVALIDATING_TYPES = new Set([
  CREATE_RESOURCE_TASK_SUCCESS,
  ASSIGN_RESOURCE_TASK_SUCCESS,
  UPDATE_RESOURCE_TASK_SUCCESS,
  UPDATE_USER_TASK,
  UPDATE_ACTIVE,
  SET_ALL_AS_ACTIVE,
  SET_ALL_AS_INACTIVE,
  TOP_DOWN_RESOURCE_TASK_UPDATE,
  DELETE_RESOURCE_TASK_SUCCESS,
  DELETE_BATCH_OF_RESOURCE_TASKS,
  UPDATE_USER_TASK_STATUS_AND_PROGRESS,
]);

// Look-ahead row DTOs may embed the RT's custom-column values (`row.customColumns`, keyed by
// column name — the same shape the assign flow builds client-side). Move them onto
// `resourceTask.customColumns` so downstream consumers (mergeTaskWithCustomColumn early-return,
// convertTasks flattening) pick them up on first render instead of waiting for the separate
// whole-interval custom-columns request.
function adoptEmbeddedRtCustomColumns(rows) {
  if (Array.isArray(rows)) {
    rows.forEach((row) => {
      if (row && row.customColumns && !("customColumns" in row.resourceTask)) {
        row.resourceTask.customColumns = row.customColumns;
      }
    });
  }
  return rows;
}

// eslint-disable-next-line
function baseSchedulerReducer(state = initialState, { type, payload }) {
  switch (type) {
    case GET_RESOURCE_TASKS_REQUEST:
      return produce(state, (draftState) => {
        draftState.isFetching = true;
        draftState.tasks = [];
        draftState.error = "";
      });
    case GET_RESOURCE_TASKS_SUCCESS:
      return produce(state, (draftState) => {
        draftState.isFetching = false;
        draftState.tasks = adoptEmbeddedRtCustomColumns(payload || []);
      });
    case SET_RESOURCE_TASKS_SCOPE:
      // Delta-band scope switch: set the display window (+ coverage), re-derive the shown
      // tasks from the pool locally. Narrowing a period lands ONLY here — zero network.
      return produce(state, (draftState) => {
        draftState.tasksWindow = payload.window || null;
        draftState.tasksCoverage = payload.coverage || null;
        if (payload.resetPool) draftState.tasksPool = [];
        draftState.tasks = draftState.tasksWindow
          ? draftState.tasksPool.filter((t) => rtRowInWindow(t, draftState.tasksWindow))
          : draftState.tasksPool;
        // Settle streaming state on the current window (a pure narrow never dispatches a
        // completing chunk — see the SA reducer note).
        draftState.tasksStreaming = false;
        draftState.tasksTotalRows = draftState.tasks.length;
        draftState.isFetching = false;
        draftState.error = "";
      });
    case GET_RESOURCE_TASKS_CHUNK:
      return produce(state, (draftState) => {
        draftState.error = "";
        adoptEmbeddedRtCustomColumns(payload.rows);
        // Chunks feed the POOL (deduped by resourceTask.id); the displayed list is the pool
        // filtered by the selected window.
        const poolCountBefore = draftState.tasksPool.length;
        if (payload.replace) {
          draftState.tasksPool = payload.rows;
        } else if (payload.rows && payload.rows.length) {
          const seen = new Set(
            draftState.tasksPool.map((t) => t.resourceTask.id)
          );
          payload.rows.forEach((t) => {
            if (!seen.has(t.resourceTask.id)) {
              seen.add(t.resourceTask.id);
              draftState.tasksPool.push(t);
            }
          });
        }
        draftState.tasks = draftState.tasksWindow
          ? draftState.tasksPool.filter((t) => rtRowInWindow(t, draftState.tasksWindow))
          : draftState.tasksPool;
        // Unmask the Gantt as soon as the first batch is in (not when the last chunk lands);
        // stay "fetching" only while there's nothing to show yet.
        draftState.isFetching = !payload.complete && draftState.tasks.length === 0;
        // Streaming meta for the non-blocking "X / Y rows" chip. A band load's totalRows
        // only counts the band — offset by what was already pooled.
        draftState.tasksStreaming = !payload.complete;
        if (typeof payload.totalRows === "number") {
          draftState.tasksTotalRows = payload.band
            ? poolCountBefore + payload.totalRows
            : payload.totalRows;
        } else if (payload.complete) {
          draftState.tasksTotalRows = draftState.tasks.length;
        }
        if (payload.complete && payload.key) {
          draftState.tasksCache[payload.key] = draftState.tasks;
          draftState.tasksCacheKey = payload.key;
        }
      });
    case GET_RESOURCE_TASKS_FROM_CACHE:
      return produce(state, (draftState) => {
        draftState.tasks = draftState.tasksCache[payload.key] || [];
        draftState.tasksCacheKey = payload.key;
        draftState.isFetching = false;
        draftState.error = "";
      });
    case GET_RESOURCE_TASKS_ERROR:
      return produce(state, (draftState) => {
        draftState.isFetching = false;
        draftState.error = payload;
      });
    case GET_RESOURCE_TASKS_DASHBOARD_REQUEST:
      return produce(state, (draftState) => {
        draftState.isDashboardFetching = true;
        draftState.error = "";
      });
    case GET_RESOURCE_TASKS_DASHBOARD_SUCCESS:
      return produce(state, (draftState) => {
        draftState.isDashboardFetching = false;
        draftState.dashboard = payload || [];
      });
    case GET_RESOURCE_TASKS_DASHBOARD_ERROR:
      return produce(state, (draftState) => {
        draftState.isDashboardFetching = false;
        draftState.error = payload;
      });
    case CREATE_RESOURCE_TASK_REQUEST:
      return {
        ...state,
        isUpdating: true,
        error: "",
      };
    case CREATE_RESOURCE_TASK_SUCCESS:
      return {
        ...state,
        isUpdating: false,
        tasks: [...state.tasks, payload],
      };
    case CREATE_RESOURCE_TASK_ERROR:
      return {
        ...state,
        isUpdating: false,
        error: "",
      };
    case ASSIGN_RESOURCE_TASK_REQUEST:
      return {
        ...state,
        isUpdating: true,
        error: "",
      };
    case ASSIGN_RESOURCE_TASK_SUCCESS:
      return {
        ...state,
        isUpdating: false,
        tasks: [...state.tasks, payload.child].map((val) => {
          const isUpdated = payload.updatedTasks.some(
            ({ resourceTask }) => resourceTask.id === val.resourceTask.id
          );
          if (!isUpdated) return val;
          else
            return payload.updatedTasks.find(
              ({ resourceTask }) => resourceTask.id === val.resourceTask.id
            );
        }),
      };
    case ASSIGN_RESOURCE_TASK_ERROR:
      return {
        ...state,
        isUpdating: false,
        error: "",
      };
    case UPDATE_RESOURCE_TASK_REQUEST:
      return {
        ...state,
        isUpdating: true,
        error: "",
      };
    case UPDATE_RESOURCE_TASK_SUCCESS:
      return produce(state, (draftState) => {
        draftState.isUpdating = false;
        draftState.tasks = [...state.tasks].map((task) => {
          if (task.resourceTask.id === payload.child.resourceTask.id)
            return payload.child;
          return task;
        });
      });
    case UPDATE_USER_TASK:
      return produce(state, (draftState) => {
        draftState.tasks = draftState.tasks.map((task) => {
          if (task.resourceTask.id !== payload.id) return task;
          const newTask = { ...task };
          newTask.resourceTask.name = payload.name;
          newTask.resourceTask.start = payload.start;
          newTask.resourceTask.finish = payload.finish;
          newTask.resourceTask.progress = payload.progress;
          newTask.resourceTask.status = payload.status;
          newTask.resourceTask.notes = payload.notes;
          return newTask;
        });
      });
    case UPDATE_RESOURCE_TASK_ERROR:
      return {
        ...state,
        isUpdating: false,
        error: payload,
      };
    case UPDATE_ACTIVE:
      return {
        ...state,
        isUpdating: false,
        tasks: state.tasks.map((task) => {
          if (task.resourceTask.id == payload) {
            return {
              ...task,
              resourceTask: {
                ...task.resourceTask,
                isActive: !task.resourceTask.isActive,
              },
            };
          } else return task;
        }),
      };
    case SET_ALL_AS_ACTIVE:
      return {
        ...state,
        isUpdating: false,
        tasks: state.tasks.map((task) => {
          if (payload.includes(task.resourceTask.id)) {
            return {
              ...task,
              resourceTask: {
                ...task.resourceTask,
                isActive: true,
              },
            };
          } else return task;
        }),
      };
    case SET_ALL_AS_INACTIVE:
      return {
        ...state,
        isUpdating: false,
        tasks: state.tasks.map((task) => {
          if (payload.includes(task.resourceTask.id)) {
            return {
              ...task,
              resourceTask: {
                ...task.resourceTask,
                isActive: false,
              },
            };
          } else return task;
        }),
      };
    case TOP_DOWN_RESOURCE_TASK_UPDATE:
      return produce(state, (draftState) => {
        // constrcuting an object to avoid big computations on array (find, filter etc...)
        const mapRTs = payload.reduce(
          (map, rt) => ({ ...map, [rt.id]: rt }),
          {}
        );
        draftState.tasks = draftState.tasks.map((task) => {
          const rtId = task.resourceTask.id;
          if (!(rtId in mapRTs)) return task;
          // Keep the task's other top-level fields (projectName, readOnly, …); only the
          // resourceTask is refreshed. Returning `{ resourceTask }` alone dropped projectName
          // and broke downstream project/team filtering and display.
          return { ...task, resourceTask: mapRTs[rtId] };
        });
      });
    case DELETE_RESOURCE_TASK_REQUEST:
      return {
        ...state,
        isUpdating: true,
        error: "",
      };
    case DELETE_RESOURCE_TASK_SUCCESS:
      return produce(state, (draftState) => {
        draftState.isUpdating = false;
        draftState.tasks = draftState.tasks.reduce((newTasks, val) => {
          if (val.resourceTask.id === payload.childId) return newTasks;
          const ids = payload.updatedTasks.map(
            ({ resourceTask }) => resourceTask.id
          );
          return [
            ...newTasks,
            ids.includes(val.resourceTask.id)
              ? payload.updatedTasks.find(
                ({ resourceTask }) => resourceTask.id === val.resourceTask.id
              )
              : val,
          ];
        }, []);
      });
    case DELETE_RESOURCE_TASK_ERROR:
      return {
        ...state,
        isUpdating: false,
        error: payload,
      };
    case DELETE_BATCH_OF_RESOURCE_TASKS:
      return produce(state, draftState => {
        draftState.tasks = draftState.tasks.filter(task => !payload.includes(task.resourceTask.id));
      });
    case CHECK_IS_MANAGER_SUCCESS:
      return {
        ...state,
        isManager: payload,
      };
    case CHECK_IS_MANAGER_ERROR:
      return {
        ...state,
        error: payload,
      };
    case GET_RESOURCE_TASKS_BY_SCENARIO_ID_REQUEST:
      return produce(state, (draftState) => {
        draftState.isFetching = true;
        draftState.tasks = [];
      });
    case GET_RESOURCE_TASKS_BY_SCENARIO_ID_SUCCESS:
      return produce(state, (draftState) => {
        draftState.isFetching = false;
        draftState.tasks = payload;
      });
    case GET_RESOURCE_TASKS_BY_SCENARIO_ID_ERROR:
      return produce(state, (draftState) => {
        draftState.isFetching = false;
        draftState.error = payload;
      });
    case GET_RESOURCE_TASKS_CUSTOM_COLUMNS_REQUEST:
      return produce(state, (draftState) => {
        draftState.isFetchingColumns = true;
        draftState.error = "";
      });
    case GET_RESOURCE_TASKS_CUSTOM_COLUMNS_SUCCESS:
      return produce(state, (draftState) => {
        draftState.isFetchingColumns = false;
        draftState.customColumns = payload || [];
      });
    case GET_RESOURCE_TASKS_CUSTOM_COLUMNS_ERROR:
      return produce(state, (draftState) => {
        draftState.isFetchingColumns = false;
        draftState.error = payload;
      });
    case GET_RESOURCE_TASKS_CUSTOM_COLUMN_NAMES_REQUEST:
      return produce(state, (draftState) => {
        draftState.isFetchingColumnNames = true;
        draftState.error = "";
      });
    case GET_RESOURCE_TASKS_CUSTOM_COLUMN_NAMES_SUCCESS:
      return produce(state, (draftState) => {
        draftState.isFetchingColumnNames = false;
        draftState.customColumnNames = payload || [];
      });
    case GET_RESOURCE_TASKS_CUSTOM_COLUMN_NAMES_ERROR:
      return produce(state, (draftState) => {
        draftState.isFetchingColumnNames = false;
        draftState.error = payload;
      });
    case ADD_RT_CUSTOM_COLUMNS:
      return produce(state, (draftState) => {
        draftState.customColumns = [...draftState.customColumns, ...payload];
      });
    case UPDATE_RT_CUSTOM_COLUMN_VALUE:
      return produce(state, (draftState) => {
        const { rtId, columnName, columnValue } = payload;
        draftState.customColumns = draftState.customColumns.map((column) => {
          if (+column.id === +rtId) {
            return { ...column, [columnName]: columnValue };
          }
          return column;
        });
      });
    case UPDATE_USER_TASK_STATUS_AND_PROGRESS:
      return produce(state, (draftState) => {
        draftState.tasks = draftState.tasks.map((task) => {
          if (task.resourceTask.id === payload.id) {
            task.resourceTask.progress = payload.progress;
            task.resourceTask.status = payload.status;
          }
          return task;
        });
      });
    default:
      return state;
  }
}

// eslint-disable-next-line
export default function schedulerReducer(state = initialState, action = {}) {
  const next = baseSchedulerReducer(state, action);
  if (
    next !== state &&
    TASKS_CACHE_INVALIDATING_TYPES.has(action.type) &&
    next.tasksCache &&
    Object.keys(next.tasksCache).length > 0
  ) {
    // Also drop the delta-band coverage and resync the pool to the DISPLAYED tasks: the
    // mutation reducers only touch `tasks`, so the pool would otherwise go stale. With
    // coverage null the next period change is a full reload (correctness over reuse).
    return {
      ...next,
      tasksCache: {},
      tasksCacheKey: null,
      tasksCoverage: null,
      tasksPool: next.tasks,
    };
  }
  return next;
}
